/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { defineMessages, useIntl } from 'react-intl-next';

import { Inline } from '@atlaskit/primitives';
import SectionIcon from '@atlaskit/icon-lab/core/section';
import { token } from '@atlaskit/tokens';
import { Pressable, Text } from '@atlaskit/primitives/compiled';
import { cssMap, jsx } from '@atlaskit/css';
import { EndOfPageRecommendationInfoPopup } from './EndOfPageRecommendationInfoPopup';
import type { TriggerProps } from '@atlaskit/popup';

const i18n = defineMessages({
	headerTitle: {
		id: 'page-recommendations.end-of-page-recommendation-compact-header.title',
		defaultMessage: 'Related content',
		description: 'The title of the end of page recommendation compact header.',
	},
	sendFeedback: {
		id: 'page-recommendations.end-of-page-recommendation-compact-header.send-feedback',
		defaultMessage: 'Send feedback',
		description: 'The text of the send feedback button.',
	},
});
const styles = cssMap({
	pressable: {
		font: token('font.body.small'),
		backgroundColor: token('color.background.neutral.subtle'),
		paddingTop: token('space.0'),
		paddingRight: token('space.0'),
		paddingBottom: token('space.0'),
		paddingLeft: token('space.0'),

		'&:hover': {
			textDecoration: 'underline',
		},
		'&:active': {
			color: token('color.link.pressed'),
		},
	},
	container: {
		paddingBottom: token('space.200'),
	},
});

type EndOfPageRecommendationCompactHeaderProps = {
	sendFeedback: () => void;
	toggleInfo: () => void;
	isInfoOpen: boolean;
	handleInfoButtonRef: (triggerProps: TriggerProps) => (node: HTMLButtonElement | null) => void;
};

export const EndOfPageRecommendationCompactHeader = ({
	sendFeedback,
	toggleInfo,
	isInfoOpen,
	handleInfoButtonRef,
}: EndOfPageRecommendationCompactHeaderProps) => {
	const { formatMessage } = useIntl();
	return (
		<Inline
			alignBlock="center"
			spread="space-between"
			xcss={styles.container}
			testId="eop-compact-header"
		>
			<Inline space="space.100" alignBlock="center">
				<SectionIcon color={token('color.icon.subtlest')} label="" />
				<Inline space="space.050" alignBlock="center">
					<Text size="medium" weight="medium">
						{formatMessage(i18n.headerTitle)}
					</Text>
					<EndOfPageRecommendationInfoPopup
						toggleInfo={toggleInfo}
						showFeedbackButton={false}
						isInfoOpen={isInfoOpen}
						handleInfoButtonRef={handleInfoButtonRef}
					/>
				</Inline>
			</Inline>
			<Pressable onClick={sendFeedback} xcss={styles.pressable}>
				{formatMessage(i18n.sendFeedback)}
			</Pressable>
		</Inline>
	);
};
